'use client'

import { useEffect, useRef } from 'react'
import { useSession } from '@clerk/clerk-react'
import { useAuthIdentity, useAuthState } from '@/contexts/AuthContext'

/**
 * Detects organization switches and forces JWT token refresh to keep
 * JWT token org_id in sync with UI session orgId.
 *
 * This prevents RLS policy violations when users switch organizations
 * but the JWT token hasn't been refreshed yet.
 */
export function OrganizationTokenSync() {
  const { organizationId } = useAuthIdentity()
  const { isLoaded } = useAuthState()
  const { session } = useSession()
  const previousOrgIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isLoaded || !session) return

    // On first load, just store the current org ID
    if (previousOrgIdRef.current === null) {
      previousOrgIdRef.current = organizationId || null
      return
    }

    // If organization changed, force token refresh
    if (previousOrgIdRef.current !== organizationId) {
      console.log('🔄 Organization changed, forcing token refresh:', {
        from: previousOrgIdRef.current,
        to: organizationId || null,
      })

      // Force token refresh to get new JWT with correct org_id
      // Refresh the token for the Supabase template so org claims update
      session
        .getToken({ skipCache: true, template: 'supabase' })
        .then(() => {
          console.log('✅ Token refreshed successfully')
        })
        .catch(error => {
          console.error('❌ Failed to refresh token:', error)
        })

      // Update the ref for next comparison
      previousOrgIdRef.current = organizationId || null
    }
  }, [organizationId, isLoaded, session])

  // This component renders nothing - it just monitors org changes
  return null
}
